import { Flex, message } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetGxPReportsList } from '@/apis/settings';
import { MenuLabels, SubMenuLabels } from '@/common/enum';
import { useToken } from '@/common/helper';
import { GxPSortOptions } from '@/common/static_data';
import { getRangeText } from '@/common/utils/getRangeText';
import { CommonDrawer, CustomButton } from '@/components';
import { useSpin } from '@/components/SpinContext';
import { useMenuPermissions } from '@/hooks';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useAiHistoryFiltersController } from '@/pages/AiHistory/useHistoryFilterController';
import ShipmentsFilterPanel from '@/pages/Shipments/ControlTower/components/ShipmentFiltersPanel';
import ShipmentSort from '@/pages/Shipments/ControlTower/components/ShipmentSort';
import { type SortOrder } from '@/pages/Shipments/ControlTower/hooks/useShipmentSort';
import { useSelector } from 'react-redux';
import { GxPReportTable, GxPSettingDrawer } from './components';
import GxPTabStrip from './components/TabStrip';
import {
  GxPPageprops,
  GxPReportRecord,
  GxPReportTabCounts,
  GxPReportTabKey,
} from './components/types';
import './GxP.scss';
import { useSortingHook } from './hooks/useSortingHook';
import { gxpFiltersConfig } from './utils/gxpFiltersConfig';

const GxPReportPage: React.FC<GxPPageprops> = ({ title }) => {
  const { t } = useTranslation('aiHistory');
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const filters = useAiHistoryFiltersController();

  const features = useSelector(
    (state: {
      menu: {
        features: Array<{ name: string; permissions?: { is_view?: boolean } }>;
      };
    }) => state.menu.features
  );
  const gxp_settings_permissons = features?.find(
    (feature) => feature.name === 'GxP settings'
  );

  const gxp_page_permissions = useMenuPermissions(
    MenuLabels.Settings,
    SubMenuLabels.Gxp_reports
  );

  const [GxPTableList, setGxPTableList] = useState<GxPReportRecord[]>([]);
  const [tabCounts, setTabCounts] = useState<GxPReportTabCounts>({
    all: 0,
    to_review: 0,
    published: 0,
    sent: 0,
  });

  const [page, setPage] = useState<number>(1);
  const [maxPageSize, setMaxPageSize] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(50);

  const REPORT_PAGE_SIZE = 50;

  const [selectAllAcrossFilters] = useState(false);
  const dynamicDropdownHeight = `calc(100vh - 400px)`;

  // Resolved backend tab_id for the active tab: the saved filter set's own id
  // for custom tabs (matches how Shipments resolves apiTabId), empty for the
  // default "All" tab. Updated by handleTabChange below.
  const activeApiTabIdRef = useRef<GxPReportTabKey>('all');

  // Sort defaults for the active tab — refreshed from the tab's own
  // sort_key/sort_order (custom tabs only) inside handleTabChange below.
  const [tabSortDefaults, setTabSortDefaults] = useState<{
    sortKey: string;
    sortOrder: SortOrder;
  }>({ sortKey: 'delivery_date', sortOrder: 'descending' });

  const {
    sortKey,
    sortOrder,
    sortMenu,
    selectedSortLabel,
    handleSortOrderToggle,
  } = useSortingHook({
    tabId: activeApiTabIdRef.current,
    initialSortKey: tabSortDefaults.sortKey,
    initialSortOrder: tabSortDefaults.sortOrder,
    sortOptions: GxPSortOptions,
    onUserSortChange: ({ sortKey: nextSortKey, sortOrder: nextSortOrder }) => {
      suppressNextFilterEffectFetchRef.current = true;
      setPage(1);
      fetchGxPData(
        1,
        searchQueryRef.current,
        undefined,
        nextSortKey,
        nextSortOrder
      );
    },
  });

  // Pass apiTabId explicitly so the correct value is used on the same tick as setActiveTabId
  const fetchGxPData = useCallback(
    (
      pageArg: number,
      search: string,
      apiTabIdOverride?: GxPReportTabKey,
      sortKeyOverride?: string,
      sortOrderOverride?: SortOrder
    ) => {
      const resolvedApiTabId = apiTabIdOverride ?? activeApiTabIdRef.current;
      const resolvedSortKey = sortKeyOverride ?? sortKey;
      const resolvedSortOrder = sortOrderOverride ?? sortOrder;
      showSpin();

      const body: Parameters<typeof GetGxPReportsList>[1] = {
        ...(filters.filtersPayloadRef.current ?? {}),
        page: pageArg,
        page_size: REPORT_PAGE_SIZE,
        search,
        tab: resolvedApiTabId,
        sort_by: resolvedSortKey,
        sort_order: resolvedSortOrder === 'ascending' ? 'asc' : 'desc',
      };

      setGxPTableList([]);
      GetGxPReportsList(token, body)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            setGxPTableList(data?.data?.results || []);
            setTabCounts(
              data?.data?.tab_counts || {
                all: 0,
                to_review: 0,
                published: 0,
                sent: 0,
              }
            );
            setTotalPages(data?.page_count || 1);
            setMaxPageSize(data?.data?.total_count || 0);
          } else {
            message.error(data?.message || t('errorFetchData'));
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error(t('errorFetchData'));
        })
        .finally(() => {
          hideSpin();
        });
    },
    [
      showSpin,
      token,
      unauthorize,
      hideSpin,
      t,
      sortKey,
      sortOrder,
      filters.filtersPayloadRef,
    ]
  );

  const searchQueryRef = useRef(filters.searchQuery);
  const suppressNextFilterEffectFetchRef = useRef(false);

  useEffect(() => {
    searchQueryRef.current = filters.searchQuery;
  }, [filters.searchQuery]);

  useEffect(() => {
    if (suppressNextFilterEffectFetchRef.current) {
      suppressNextFilterEffectFetchRef.current = false;
      return;
    }

    fetchGxPData(page, searchQueryRef.current);
  }, [page, fetchGxPData]);

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  // ------------------------------------------------------------------
  // Tab change — resolve the backend tab_id from the tab's own saved filter
  // set id (not the tab pin id), same as Shipments' apiTabId resolution, so
  // the table reflects a tab's filters immediately after editing them.
  // ------------------------------------------------------------------
  const handleTabChange = useCallback(
    (tabId: GxPReportTabKey) => {
      activeApiTabIdRef.current = tabId;

      const tabSortKey = 'delivery_date';
      const tabSortOrder = 'descending';
      setTabSortDefaults({ sortKey: tabSortKey, sortOrder: tabSortOrder });

      suppressNextFilterEffectFetchRef.current = true;
      setPage(1);
      fetchGxPData(1, searchQueryRef.current, tabId, tabSortKey, tabSortOrder);
    },
    [fetchGxPData]
  );

  // ------------------------------------------------------------------
  // Filter panel search
  // ------------------------------------------------------------------
  const handleRunSearchFromPanel = useCallback(
    ({
      query,
      page: p,
    }: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
    }) => {
      const nextPage = p ?? 1;
      filters.setSearchQuery(query);
      suppressNextFilterEffectFetchRef.current = true;
      setPage(nextPage);
      fetchGxPData(nextPage, query);
    },
    [fetchGxPData, filters]
  );

  // ------------------------------------------------------------------
  // Settings drawer
  // ------------------------------------------------------------------
  const [showDrawer, setShowDrawer] = useState(false);

  return (
    <>
      <div className={'pageWrapper'}>
        <Flex
          align='center'
          justify='space-between'
          className={`dashboard-title CT-header-title mb-4`}
        >
          <div className='gxp-header-wrapper-flex-container'>
            <div className={'pageHeader mb-2'}>
              <p className={`zl-heading-24 pageTitle`}>{title}</p>
            </div>

            <div className={'tabStripWrapper'}>
              <GxPTabStrip
                activeTabId={activeApiTabIdRef.current}
                tabCounts={tabCounts}
                onTabChange={handleTabChange}
              />
            </div>
          </div>
        </Flex>

        {/* Toolbar */}
        <div className={'toolbar'}>
          <Flex align='end' wrap gap={12} className={'toolbarCountRow'}>
            <div className='zl-para-400-12 color-5e'>
              {getRangeText(rangeStart, rangeEnd, totalCount)}
            </div>
          </Flex>

          <Flex gap={12} className={'toolbarActionRow'}>
            <div className={'filtersCol'}>
              <ShipmentsFilterPanel
                dropdownMaxHeight={dynamicDropdownHeight}
                showSentimentFilter={false}
                savedFilterKey={'gxp_reports'}
                filterConfig={gxpFiltersConfig}
                showAiStateFilter={true}
                token={token}
                permissions={null}
                appliedFilters={filters.appliedFilters}
                setAppliedFilters={filters.setAppliedFilters}
                filterArray={filters.filterArray}
                setFilterArray={filters.setFilterArray}
                setFiltersPayload={filters.setFiltersPayload}
                searchQuery={filters.searchQuery}
                setSearchQuery={(val) => {
                  filters.setSearchQuery(val);
                  setPage(1);
                }}
                isSearchVisible={filters.isSearchVisible}
                setIsSearchVisible={filters.setIsSearchVisible}
                page={page}
                pageSize={pageSize}
                onRunSearch={handleRunSearchFromPanel}
                registerHandleApplyFilters={() => {}}
              />
            </div>
            <Flex gap={12}>
              <ShipmentSort
                sortMenu={sortMenu}
                selectedSortLabel={selectedSortLabel}
                sortOrder={sortOrder}
                sortKey={sortKey}
                onToggleOrder={handleSortOrderToggle}
              />
              {gxp_settings_permissons?.permissions?.is_view &&
                (gxp_page_permissions?.is_update ||
                  gxp_page_permissions?.is_create) && (
                  <CustomButton
                    text={'Edit GxP settings'}
                    bgColor='var(--gray-300)'
                    color='var(--text-primary)'
                    fontSize='12px'
                    fontWeight='600'
                    height={'28px'}
                    borderRadius='20px'
                    padding='6px 14px'
                    boxShadow='none'
                    iconPosition='left'
                    onClick={() => setShowDrawer(true)}
                  />
                )}
            </Flex>
          </Flex>
        </div>

        {/* Table */}
        <div className={'tableArea'}>
          <GxPReportTable
            onRefresh={() => fetchGxPData(page, searchQueryRef.current)}
            GxPTableList={GxPTableList}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            totalPages={totalPages}
            maxPageSize={maxPageSize}
            selectAllAcrossFilters={selectAllAcrossFilters}
            activeTab={activeApiTabIdRef.current}
          />
        </div>
      </div>

      {/* Settings drawer */}
      {showDrawer && (
        <CommonDrawer open={showDrawer} onClose={() => setShowDrawer(false)}>
          <GxPSettingDrawer onClose={() => setShowDrawer(false)} />
        </CommonDrawer>
      )}
    </>
  );
};

export default GxPReportPage;
